import React, { useMemo, useState } from 'react';
import { MenuItem, TextField, IconButton, Tooltip, InputAdornment } from '@mui/material';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ViewListRoundedIcon from '@mui/icons-material/ViewListRounded';
import GridViewRoundedIcon from '@mui/icons-material/GridViewRounded';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import EventRepeatOutlinedIcon from '@mui/icons-material/EventRepeatOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import FilterAltOffOutlinedIcon from '@mui/icons-material/FilterAltOffOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import PersonSearchOutlinedIcon from '@mui/icons-material/PersonSearchOutlined';
import HowToRegOutlinedIcon from '@mui/icons-material/HowToRegOutlined';
import PersonOffOutlinedIcon from '@mui/icons-material/PersonOffOutlined';
import moment from 'moment';
import NoData from '../../loaders/NoData';
import SkeletonLoader from '../../loaders/Skeleton';
import CandidateCard from './CandidateCard';
import ResumeViewModal from './ResumeViewModal';
import ViewCandidateDetailsModal from './ViewCandidateDetailsModal';
import { downloadLink } from '../../helpers/constFIles';
/* the job description is editor HTML; JobJD already exports the readable
   snippet helper for it, so there is one definition of that, not two */
import { excerpt } from '../JobJD/JobDescriptionModal';
import {
    STAGES, statusOf, stageOf, accentOf, nextRoundNumber, splitSkills, initials
} from './pipeline';
import './Pipeline.scss';

/* The tiles across the top. "All" first, then one per stage, each one also
   the control that filters down to it. */
const TILE_ICONS = {
    all: GroupsOutlinedIcon,
    pool: PersonSearchOutlinedIcon,
    scheduled: EventAvailableOutlinedIcon,
    selected: HowToRegOutlinedIcon,
    rejected: PersonOffOutlinedIcon,
};

const TILES = [
    { key: 'all', label: 'All candidates', tone: 'is-slate' },
    ...STAGES.map((stage) => ({ key: stage.key, label: stage.label, tone: stage.tone })),
];

const SORTS = [
    { value: 'stage', label: 'Pipeline stage' },
    { value: 'upcoming', label: 'Next interview' },
    { value: 'name', label: 'Name (A–Z)' },
];

/* Row entry is staggered, but only for the first screenful — past that the
   delay would read as the table being slow rather than as an animation. */
const stagger = (index) => ({ animationDelay: `${Math.min(index, 14) * 35}ms` });

/* The Job column has room for a title and nothing else, so the rest of the
   job — what it asks for, how many rounds, when it lapses — is a hover away
   rather than another click. Built on the shared `wh-tip` tooltip. */
const JobTip = ({ job }) => {
    const span = [job.experienceFrom, job.experienceTo]
        .filter((value) => value !== '' && value !== null && value !== undefined);

    const facts = [
        span.length > 0 && { label: 'Experience', value: `${span.join(' – ')} yrs` },
        job.numberOfRounds && { label: 'Rounds', value: job.numberOfRounds },
        job.expiredOn && { label: 'Expires', value: moment(job.expiredOn).format('DD MMM YYYY') },
    ].filter(Boolean);

    const skills = splitSkills(job.skills);
    const text = excerpt(job.requirements, 420);

    return (
        <div className="wh-tip-list wh-job-tip">
            <b>{job.jobTitle}</b>

            {facts.length > 0 && (
                <ul>
                    {facts.map((fact) => (
                        <li key={fact.label}>
                            <span>{fact.label}</span><em>{fact.value}</em>
                        </li>
                    ))}
                </ul>
            )}

            {skills.length > 0 && <p className="wh-job-tip-skills">{skills.join(' · ')}</p>}

            <p className="wh-job-tip-text">
                {text || 'No description recorded for this job.'}
            </p>
        </div>
    );
};

const matchesQuery = (candidate, query) => {
    if (!query) return true;
    const needle = query.trim().toLowerCase();
    return [
        candidate.name, candidate.candidateID, candidate.email, candidate.phone,
        candidate.skills, candidate.job?.jobTitle,
        statusOf(candidate).interviewer?.employeeName,
    ].some((field) => String(field || '').toLowerCase().includes(needle));
};

const PipelineBoard = ({ candidates, isLoad, onSchedule, onEdit }) => {

    const [view, setView] = useState('table');
    const [stage, setStage] = useState('all');
    const [query, setQuery] = useState('');
    const [round, setRound] = useState('all');
    const [sort, setSort] = useState('stage');

    const [candidate, setCandidate] = useState(null);
    const [resumeView, setResumeView] = useState(null);

    /* Which round numbers exist at all, so the filter only ever offers rounds
       somebody is actually sitting at. */
    const roundOptions = useMemo(() => {
        const seen = new Set();
        candidates.forEach((item) => {
            const current = statusOf(item).round;
            if (current?.round) seen.add(String(current.round));
        });
        return [...seen].sort((a, b) => Number(a) - Number(b));
    }, [candidates]);

    /* Everything except the stage filter. The tile counts are taken from this,
       so they stay honest while a search is running. */
    const searched = useMemo(() => candidates.filter((item) => {
        if (!matchesQuery(item, query)) return false;
        if (round !== 'all' && String(statusOf(item).round?.round || '') !== round) return false;
        return true;
    }), [candidates, query, round]);

    const counts = useMemo(() => {
        const tally = { all: searched.length, pool: 0, scheduled: 0, selected: 0, rejected: 0 };
        searched.forEach((item) => { tally[stageOf(item)] += 1; });
        return tally;
    }, [searched]);

    const rows = useMemo(() => {
        const order = ['pool', 'scheduled', 'selected', 'rejected'];
        const filtered = stage === 'all'
            ? searched
            : searched.filter((item) => stageOf(item) === stage);

        const sorted = [...filtered];
        if (sort === 'name') {
            sorted.sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        } else if (sort === 'upcoming') {
            /* soonest first, and anyone with nothing booked after everybody
               who has — an empty date must not sort as 1970 */
            sorted.sort((a, b) => {
                const left = statusOf(a).when;
                const right = statusOf(b).when;
                if (!left && !right) return 0;
                if (!left) return 1;
                if (!right) return -1;
                return new Date(left) - new Date(right);
            });
        } else {
            sorted.sort((a, b) => order.indexOf(stageOf(a)) - order.indexOf(stageOf(b)));
        }
        return sorted;
    }, [searched, stage, sort]);

    /* what is actually coming up, said once above the list */
    const upcoming = useMemo(() => rows.filter((item) => {
        const when = statusOf(item).when;
        return when && moment(when).isBetween(moment(), moment().add(7, 'days'));
    }).length, [rows]);

    const isFiltered = stage !== 'all' || !!query || round !== 'all';

    const clearFilters = () => { setStage('all'); setQuery(''); setRound('all'); };

    const openResume = (data) => setResumeView({
        filePath: downloadLink + data.resumeUrl,
        filetype: String(data.resumeUrl).split('.').at(-1),
        resumeName: data.name,
    });

    return (
        <div className="wh-pipeline">

            {/* ---------- the tiles ---------- */}
            <div className="wh-pipeline-tiles">
                {TILES.map((tile, index) => {
                    const Icon = TILE_ICONS[tile.key];
                    return (
                        <button type="button" key={tile.key} style={stagger(index)}
                            className={`wh-pipeline-tile ${tile.tone}${stage === tile.key ? ' is-active' : ''}`}
                            aria-pressed={stage === tile.key}
                            onClick={() => setStage(tile.key)}>
                            <span className="wh-pipeline-tile-icon"><Icon /></span>
                            <b>{counts[tile.key]}</b>
                            <span className="wh-pipeline-tile-label">{tile.label}</span>
                        </button>
                    );
                })}
            </div>

            {/* ---------- the filters ---------- */}
            <div className="wh-filter-bar wh-pipeline-filters">
                <TextField size="small" label="Search" value={query}
                    className="wh-pipeline-search"
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Name, ID, email, skill, interviewer"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment>
                        ),
                    }} />

                <TextField select size="small" label="Round" value={round}
                    onChange={(event) => setRound(event.target.value)}>
                    <MenuItem value="all">Any round</MenuItem>
                    {roundOptions.map((value) =>
                        <MenuItem value={value} key={value}>Round {value}</MenuItem>)}
                </TextField>

                <TextField select size="small" label="Sort by" value={sort}
                    onChange={(event) => setSort(event.target.value)}>
                    {SORTS.map((option) =>
                        <MenuItem value={option.value} key={option.value}>{option.label}</MenuItem>)}
                </TextField>

                {isFiltered && (
                    <Tooltip title="Clear the filters" placement="top" arrow>
                        <IconButton onClick={clearFilters} className="wh-pipeline-clear">
                            <FilterAltOffOutlinedIcon />
                        </IconButton>
                    </Tooltip>
                )}

                <div className="wh-segmented wh-filter-bar-end" role="group" aria-label="Change view">
                    <button type="button" aria-pressed={view === 'table'}
                        className={view === 'table' ? 'is-active' : ''}
                        onClick={() => setView('table')}>
                        <ViewListRoundedIcon /> Table
                    </button>
                    <button type="button" aria-pressed={view === 'tiles'}
                        className={view === 'tiles' ? 'is-active' : ''}
                        onClick={() => setView('tiles')}>
                        <GridViewRoundedIcon /> Tiles
                    </button>
                </div>
            </div>

            {!isLoad && candidates.length > 0 && (
                <p className="wh-report-recap wh-pipeline-recap">
                    <EventAvailableOutlinedIcon />
                    Showing {rows.length} of {candidates.length} candidates
                    <span>·</span>
                    {upcoming} interview{upcoming === 1 ? '' : 's'} in the next 7 days
                </p>
            )}

            {isLoad
                ? <SkeletonLoader />
                : rows.length === 0
                    ? <NoData
                        title={isFiltered ? 'Nothing matches those filters' : 'No candidates yet'}
                        message={isFiltered
                            ? 'Widen the search or clear the filters to see the whole pipeline.'
                            : 'Add a candidate and they show up here with their stage.'} />

                    /* The key restarts the entry animation on a view or filter
                       change, so the new list arrives rather than just appearing. */
                    : view === 'tiles'
                        ? <div className="wh-pipeline-body" key={`tiles-${stage}-${sort}`}>
                            <CandidateCard pool={rows} showInterview showOutcome showEditBtn
                                accent={accentOf} onEdit={onEdit} onSchedule={onSchedule} />
                        </div>

                        : <div className="wh-pipeline-body" key={`table-${stage}-${sort}`}>
                            <TableContainer>
                                <Table stickyHeader>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Candidate</TableCell>
                                            <TableCell>Job</TableCell>
                                            <TableCell>Stage</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell>Interviewer</TableCell>
                                            <TableCell>When</TableCell>
                                            <TableCell>Skills</TableCell>
                                            <TableCell align="right">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.map((data, index) => {
                                            const status = statusOf(data);
                                            const skills = splitSkills(data.skills);
                                            const next = nextRoundNumber(data);

                                            return (
                                                <TableRow key={data.candidateID || index}
                                                    className="wh-pipeline-row" style={stagger(index)}>

                                                    <TableCell>
                                                        <div className="wh-pipeline-who">
                                                            <span className={`wh-avatar ${status.accent}`}>
                                                                {initials(data.name)}
                                                            </span>
                                                            <div>
                                                                <b title={data.name}>{data.name || '--'}</b>
                                                                <span>{data.candidateID}</span>
                                                            </div>
                                                        </div>
                                                    </TableCell>

                                                    <TableCell>
                                                        {data.job?.jobTitle
                                                            ? <Tooltip placement="top-start" arrow
                                                                classes={{ tooltip: 'wh-tip' }}
                                                                title={<JobTip job={data.job} />}>
                                                                <span className="wh-cell-clamp wh-job-cell">
                                                                    {data.job.jobTitle}
                                                                </span>
                                                            </Tooltip>
                                                            : <span className="wh-cell-muted">No job linked</span>}
                                                    </TableCell>

                                                    <TableCell>
                                                        <span className={`wh-stage-dot ${status.accent}`} />
                                                        {status.stageLabel}
                                                    </TableCell>

                                                    <TableCell>
                                                        <span className={`wh-pill wh-pill-sm ${status.tone}`}>
                                                            {status.label}
                                                        </span>
                                                        {status.total > 0 && (
                                                            <span className="wh-pipeline-progress"
                                                                title={`${status.done} of ${status.total} rounds decided`}>
                                                                <i style={{ width: `${Math.min(100, (status.done / status.total) * 100)}%` }} />
                                                            </span>
                                                        )}
                                                    </TableCell>

                                                    <TableCell>
                                                        {status.interviewer?.employeeName
                                                            ? <span className="wh-cell-clamp"
                                                                title={status.interviewer.designation || ''}>
                                                                {status.interviewer.employeeName}
                                                            </span>
                                                            : <span className="wh-cell-muted">--</span>}
                                                    </TableCell>

                                                    <TableCell>
                                                        {status.when
                                                            ? <span className="wh-pipeline-when">
                                                                <b>{moment(status.when).format('DD MMM YYYY')}</b>
                                                                <span>{moment(status.when).format('h:mm A')}</span>
                                                            </span>
                                                            : <span className="wh-cell-muted">--</span>}
                                                    </TableCell>

                                                    <TableCell>
                                                        {skills.length > 0
                                                            ? <div className="wh-pills wh-pills-narrow">
                                                                {skills.slice(0, 2).map((skill, i) =>
                                                                    <span className="wh-pill" key={i}>{skill}</span>)}
                                                                {skills.length > 2 &&
                                                                    <span className="wh-pill wh-pill-quiet"
                                                                        title={skills.join(', ')}>
                                                                        +{skills.length - 2}
                                                                    </span>}
                                                            </div>
                                                            : <span className="wh-cell-muted">--</span>}
                                                    </TableCell>

                                                    <TableCell align="right">
                                                        <div className="wh-pipeline-actions">
                                                            <Tooltip title="View candidate" placement="top" arrow>
                                                                <IconButton onClick={() => setCandidate(data)}>
                                                                    <VisibilityOutlinedIcon />
                                                                </IconButton>
                                                            </Tooltip>

                                                            <Tooltip title={data.resumeUrl ? 'Resume' : 'No resume'}
                                                                placement="top" arrow>
                                                                <IconButton disabled={!data.resumeUrl}
                                                                    onClick={() => openResume(data)}>
                                                                    <DescriptionOutlinedIcon />
                                                                </IconButton>
                                                            </Tooltip>

                                                            {status.meetingUrl && (
                                                                <Tooltip title="Join the call" placement="top" arrow>
                                                                    <IconButton component="a" target="_blank"
                                                                        rel="noreferrer" href={status.meetingUrl}>
                                                                        <VideocamOutlinedIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}

                                                            <Tooltip title="Edit candidate" placement="top" arrow>
                                                                <IconButton onClick={() => onEdit(data)}>
                                                                    <EditOutlinedIcon />
                                                                </IconButton>
                                                            </Tooltip>

                                                            {/* a decided candidate has nothing left to book */}
                                                            {status.stage !== 'selected' && status.stage !== 'rejected' && (
                                                                <Tooltip placement="top" arrow
                                                                    title={next > 1 ? `Schedule round ${next}` : 'Schedule the first round'}>
                                                                    <IconButton onClick={() => onSchedule(data)}>
                                                                        <EventRepeatOutlinedIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </div>}

            <ResumeViewModal resumeData={resumeView || { filePath: '', filetype: '', resumeName: '' }}
                show={!!resumeView} onHide={() => setResumeView(null)} />

            {candidate && (
                <ViewCandidateDetailsModal candidateID={candidate.candidateID}
                    show={!!candidate} onHide={() => setCandidate(null)} />
            )}
        </div>
    );
};

export default PipelineBoard;
